import { ChangeEvent, FC, useRef } from 'react';
import IconButton from '@mui/material/IconButton';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import type { CapturedPhoto } from '../MobileProductCapture.types.ts';
import { StepNav } from './StepNav';

type PhotoCaptureStepProps = {
    photos: CapturedPhoto[];
    onAddPhotos: (files: FileList | null) => void;
    onRemovePhoto: (id: string) => void;
    onBack: () => void;
    onNext: () => void;
};

export const PhotoCaptureStep: FC<PhotoCaptureStepProps> = ({ photos, onAddPhotos, onRemovePhoto, onBack, onNext }) => {
    const { t } = useTranslation();
    const cameraRef = useRef<HTMLInputElement>(null);
    const galleryRef = useRef<HTMLInputElement>(null);
    const filesRef = useRef<HTMLInputElement>(null);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        onAddPhotos(e.target.files);
        e.target.value = '';
    };

    return (
        <div className="mpc-step mpc-step--photos">
            <header className="mpc-step__header">
                <h2>{t('mobileProductCapture.photos.title')}</h2>
                <p>{t('mobileProductCapture.photos.subtitle')}</p>
            </header>

            <div className="mpc-source-grid">
                <button type="button" className="mpc-source-btn" onClick={() => cameraRef.current?.click()}>
                    <PhotoCameraIcon />
                    <span>{t('mobileProductCapture.photos.camera')}</span>
                </button>
                <button type="button" className="mpc-source-btn" onClick={() => galleryRef.current?.click()}>
                    <PhotoLibraryIcon />
                    <span>{t('mobileProductCapture.photos.gallery')}</span>
                </button>
                <button type="button" className="mpc-source-btn" onClick={() => filesRef.current?.click()}>
                    <FolderOpenIcon />
                    <span>{t('mobileProductCapture.photos.files')}</span>
                </button>
            </div>

            <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="mpc-hidden-input"
                onChange={handleChange}
            />
            <input
                ref={galleryRef}
                type="file"
                accept="image/*"
                multiple
                className="mpc-hidden-input"
                onChange={handleChange}
            />
            <input
                ref={filesRef}
                type="file"
                accept="image/*"
                multiple
                className="mpc-hidden-input"
                onChange={handleChange}
            />

            <div className="mpc-photo-panel">
                <div className="mpc-photo-strip-meta">
                    {photos.length === 0
                        ? t('mobileProductCapture.photos.empty')
                        : t('mobileProductCapture.photos.count', { count: photos.length })}
                </div>

                {photos.length > 0 ? (
                    <ul className="mpc-photo-strip mpc-photo-strip--wrap">
                        {photos.map((photo) => (
                            <li key={photo.id}>
                                <img src={photo.preview} alt={photo.name} />
                                <IconButton
                                    size="small"
                                    className="mpc-photo-remove"
                                    aria-label={t('mobileProductCapture.photos.remove')}
                                    onClick={() => onRemovePhoto(photo.id)}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className="mpc-photo-empty">{t('mobileProductCapture.photos.emptyHint')}</div>
                )}
            </div>

            <StepNav
                onBack={onBack}
                onNext={onNext}
                nextDisabled={photos.length === 0}
                nextLabel={t('mobileProductCapture.photos.continue')}
            />
        </div>
    );
};
